import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Lock, User, ArrowRight, CheckCircle2, AlertTriangle, RefreshCw, Eye, EyeOff, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useFingerprint } from "@/hooks/useFingerprint";
import { supabase } from "@/integrations/supabase/client";
import { validatePassword } from "@/lib/passwordValidation";
import { PasswordStrengthIndicator } from "@/components/auth/PasswordStrengthIndicator";

const benefits = [
  "1 publi grátis pra você testar",
  "Sem cartão de crédito (sério)",
  "Setup mais rápido que fazer miojo",
  "Cancele quando quiser (mas você não vai)",
];

type Step = 'form' | 'confirmation';

export default function Cadastro() {
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref');

  const [step, setStep] = useState<Step>('form');
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [emailWarning, setEmailWarning] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { toast } = useToast();
  const { signUp } = useAuth();
  const { validateEmail, recordDevice } = useFingerprint();

  const handleEmailBlur = async () => {
    if (!email || !email.includes('@')) return;
    const validation = await validateEmail(email);
    if (validation.issues.length > 0) {
      const tempEmailIssue = validation.issues.find(i => i.type === 'blocked_email');
      if (tempEmailIssue) {
        setEmailWarning('Use um email permanente (Gmail, Outlook, etc.)');
      } else if (validation.risk_score >= 50) {
        setEmailWarning('Este email pode ter restrições');
      }
    } else {
      setEmailWarning(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!acceptTerms) {
      toast({
        title: "Peraí",
        description: "Precisa aceitar os termos pra continuar. Prometo que não é pegadinha.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    const pwValidation = validatePassword(password);
    if (!pwValidation.isValid) {
      setIsLoading(false);
      toast({
        title: "Senha fraca",
        description: "A senha precisa ter 8+ caracteres, 1 maiúscula e 1 número.",
        variant: "destructive",
      });
      return;
    }

    const validation = await validateEmail(email);
    if (!validation.valid) {
      setIsLoading(false);
      toast({
        title: "Email inválido",
        description: "Use um email permanente (Gmail, Outlook, etc.)",
        variant: "destructive",
      });
      return;
    }

    const { error, user } = await signUp(email, password, fullName, referralCode || undefined);

    if (error) {
      setIsLoading(false);
      let errorMessage = error.message;
      if (error.message.includes("already registered")) {
        errorMessage = "Esse email já tá cadastrado. Esqueceu a senha?";
      }
      toast({
        title: "Ops, deu ruim",
        description: errorMessage,
        variant: "destructive",
      });
      return;
    }

    if (user?.id) {
      recordDevice(user.id).catch(console.warn);
    }

    setIsLoading(false);
    setStep('confirmation');
    toast({
      title: "Conta criada! 🎉",
      description: "Verifica teu email pra ativar.",
    });
  };

  const handleResendEmail = async () => {
    setIsResending(true);
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email });
      if (error) throw error;
      toast({
        title: "Email reenviado! 📧",
        description: "Verifica tua caixa de entrada (e spam!).",
      });
    } catch {
      toast({
        title: "Erro ao reenviar",
        description: "Tente novamente em alguns segundos.",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  const maskedEmail = email.replace(/(.{2})(.*)(@.*)/, '$1***$3');

  return (
    <div className="min-h-screen flex">
      {/* Left Panel */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <div className="absolute inset-0 gradient-hero" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,hsl(var(--gold)/0.15),transparent_60%)]" />
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="p-12 max-w-md"
          >
            <h2 className="text-3xl font-bold mb-6">
              Bora criar{" "}
              <span className="text-gradient-accent">publis que vendem</span>
            </h2>
            <ul className="space-y-4">
              {benefits.map((benefit, index) => (
                <motion.li
                  key={benefit}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-foreground">{benefit}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <AnimatePresence mode="wait">
          {step === 'form' ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-md"
            >
              <Link to="/" className="flex items-center gap-2 mb-12">
                <span className="text-2xl font-bold text-foreground tracking-tight">
                  Pub<span className="text-gradient">lee</span>
                </span>
              </Link>

              <h1 className="text-3xl font-bold mb-2">Cria sua conta</h1>
              <p className="text-muted-foreground mb-8">
                Comece grátis. Sem cartão. Sem pegadinha.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-2">Como te chamam?</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input id="name" type="text" placeholder="Seu nome" required value={fullName} onChange={(e) => setFullName(e.target.value)} className="pl-12 h-12 bg-secondary border-border" />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input id="email" type="email" placeholder="seu@email.com" required value={email} onChange={(e) => { setEmail(e.target.value); setEmailWarning(null); }} onBlur={handleEmailBlur} className={`pl-12 h-12 bg-secondary border-border ${emailWarning ? 'border-yellow-500' : ''}`} />
                  </div>
                  {emailWarning && (
                    <p className="flex items-center gap-1 text-xs text-yellow-500 mt-1">
                      <AlertTriangle className="w-3 h-3" />{emailWarning}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium mb-2">Senha</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input id="password" type={showPassword ? "text" : "password"} placeholder="Mínimo 8 caracteres" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="pl-12 pr-12 h-12 bg-secondary border-border" />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <PasswordStrengthIndicator password={password} />
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} className="w-4 h-4 mt-1 rounded border-border bg-secondary text-primary focus:ring-primary" />
                  <span className="text-sm text-muted-foreground">
                    Li e aceito os{" "}
                    <Link to="/termos" className="text-primary hover:underline">Termos de Uso</Link>{" "}e a{" "}
                    <Link to="/privacidade" className="text-primary hover:underline">Política de Privacidade</Link>
                  </span>
                </label>

                <Button type="submit" variant="hero" size="lg" className="w-full" disabled={isLoading}>
                  {isLoading ? (<><Loader2 className="w-4 h-4 animate-spin" />Criando conta...</>) : (<>Criar conta<ArrowRight className="w-4 h-4" /></>)}
                </Button>
              </form>

              <p className="text-center text-muted-foreground mt-8">
                Já tem conta?{" "}
                <Link to="/login" className="text-primary hover:underline">Entra aqui</Link>
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="confirmation"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-md"
            >
              <Link to="/" className="flex items-center gap-2 mb-12">
                <span className="text-2xl font-bold text-foreground tracking-tight">
                  Pub<span className="text-gradient">lee</span>
                </span>
              </Link>

              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Verifica teu email</h2>
                <p className="text-muted-foreground mb-6">
                  Enviamos um link de confirmação pra{" "}
                  <span className="font-medium text-foreground">{maskedEmail}</span>.
                  <br />Clica no link pra ativar tua conta.
                </p>

                <div className="space-y-3">
                  <Button onClick={handleResendEmail} variant="outline" className="w-full" disabled={isResending}>
                    {isResending ? "Reenviando..." : (<><RefreshCw className="w-4 h-4 mr-2" />Reenviar email</>)}
                  </Button>

                  <button onClick={() => setStep('form')} className="text-sm text-muted-foreground hover:text-foreground">
                    ← Voltar e corrigir email
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
